import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  ClipboardList, Download, IndianRupee, Users, RefreshCw, Loader2,
  Calendar, FileSpreadsheet, Printer,
} from 'lucide-react';
import ReceptionLayout from '../components/ReceptionLayout';
import { Button } from '../components/ui/button';
import { formatDateIST, formatDateTimeIST, todayIST } from '../utils/date';
import { useTenant } from '../contexts/TenantContext';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: API, withCredentials: true });

const DATE_FILTERS = [
  { key: 'today',     label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'week',      label: 'This Week' },
  { key: 'lastweek',  label: 'Last Week' },
  { key: 'month',     label: 'This Month' },
  { key: 'lastmonth', label: 'Last Month' },
  { key: '3months',   label: 'Last 3 Months' },
  { key: '6months',   label: 'Last 6 Months' },
  { key: 'all',       label: 'All' },
  { key: 'custom',    label: 'Custom' },
];

const STATUS_COLOR = {
  waiting:    'bg-amber-50 text-amber-700 border-amber-200',
  in_consult: 'bg-blue-50 text-blue-700 border-blue-200',
  completed:  'bg-emerald-50 text-emerald-700 border-emerald-200',
  cancelled:  'bg-red-50 text-red-700 border-red-200',
};

const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

export default function ReceptionReportsPage() {
  const [filter, setFilter] = useState('today');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [doctorFilter, setDoctorFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { tenant } = useTenant();
  const brand = tenant?.branding?.primary_color || '#0f766e';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date_filter: filter };
      if (filter === 'custom' && from && to) { params.date_from = from; params.date_to = to; }
      if (doctorFilter) params.doctor_id = doctorFilter;
      if (statusFilter) params.status = statusFilter;
      const { data } = await ax.get('/api/clinic-ops/reception/visits-report', { params });
      setData(data);
    } catch (e) {
      console.error('Report load failed', e);
      setData(null);
    }
    setLoading(false);
  }, [filter, from, to, doctorFilter, statusFilter]);

  // Auto reload on filter change (skip custom until both dates picked)
  useEffect(() => {
    if (filter === 'custom' && !(from && to)) return;
    load();
  }, [filter, from, to, doctorFilter, statusFilter, load]);

  const rows = data?.rows || [];
  const totals = data?.totals || {};

  const doctors = useMemo(() => {
    const m = new Map();
    rows.forEach(r => { if (r.doctor_id && r.doctor_name) m.set(r.doctor_id, r.doctor_name); });
    return Array.from(m, ([id, name]) => ({ id, name }));
  }, [rows]);

  const downloadCSV = () => {
    if (rows.length === 0) return;
    const head = [
      'Date/Time', 'MRN', 'Patient', 'Phone', 'Age/Gender',
      'Doctor', 'Session', 'Visit', 'Status', 'Payment',
      'Consultation ₹', 'Pharmacy ₹', 'GST ₹', 'Total ₹',
    ];
    const lines = [head.join(',')];
    rows.forEach(r => {
      const cells = [
        r.date ? formatDateTimeIST(r.date) : '',
        r.mrn,
        `"${(r.patient_name || '').replace(/"/g, '""')}"`,
        r.patient_phone || '',
        `${r.patient_age || ''} ${r.patient_gender || ''}`.trim(),
        `"${(r.doctor_name || '').replace(/"/g, '""')}"`,
        `"${(r.session_name || '').replace(/"/g, '""')}"`,
        r.visit_type || '',
        r.status || '',
        r.payment_status || '',
        r.consultation_fee || 0,
        r.pharmacy_total || 0,
        r.pharmacy_gst || 0,
        r.grand_total || 0,
      ];
      lines.push(cells.join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `visit-report-${filter}-${todayIST()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <ReceptionLayout
      pageTitle="Visit Reports"
      pageSubtitle="Date-windowed visit, payment & pharmacy summary"
      rightActions={
        <>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="report-refresh">
            {loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={downloadCSV} disabled={rows.length === 0} data-testid="report-export-csv">
            <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" /> Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.print()} disabled={rows.length === 0} data-testid="report-print">
            <Printer className="h-3.5 w-3.5 mr-1.5" /> Print
          </Button>
        </>
      }
    >
      <style>{`@media print { .no-print { display: none !important; } body { background: white; } }`}</style>

      <div className="p-7 max-w-[1500px] mx-auto space-y-5" data-testid="reception-reports">
        {/* Filter Bar */}
        <div className="bg-white border rounded-2xl p-4 shadow-sm space-y-3 no-print">
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="report-date-filter">
            {DATE_FILTERS.map(f => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition ${
                  filter === f.key
                    ? 'bg-zinc-900 text-white border-zinc-900'
                    : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400'
                }`}
                data-testid={`report-filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          {filter === 'custom' && (
            <div className="flex items-center gap-2 flex-wrap">
              <label className="text-xs text-zinc-500">From</label>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                className="border border-zinc-200 rounded-md px-2 py-1 text-sm" data-testid="report-from"/>
              <label className="text-xs text-zinc-500">To</label>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                className="border border-zinc-200 rounded-md px-2 py-1 text-sm" data-testid="report-to"/>
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            {doctors.length > 1 && (
              <select
                value={doctorFilter}
                onChange={(e) => setDoctorFilter(e.target.value)}
                className="border border-zinc-200 rounded-md px-2 py-1 text-sm"
                data-testid="report-doctor-filter"
              >
                <option value="">All doctors</option>
                {doctors.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            )}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-zinc-200 rounded-md px-2 py-1 text-sm"
              data-testid="report-status-filter"
            >
              <option value="">All statuses</option>
              <option value="waiting">Waiting</option>
              <option value="in_consult">In Consult</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>

        {/* KPI Strip */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Kpi label="Visits"     value={totals.visits || 0}                            icon={<Users />}          color={brand} />
          <Kpi label="Consult ₹"  value={fmt(totals.consultation_total)}                icon={<IndianRupee />}    color="#0891b2" />
          <Kpi label="Pharmacy ₹" value={fmt(totals.pharmacy_total)}                    icon={<ClipboardList />}  color="#7c3aed" />
          <Kpi label="GST ₹"      value={fmt(totals.pharmacy_gst)}                      icon={<ClipboardList />}  color="#f59e0b" />
          <Kpi label="Total ₹"    value={fmt(totals.grand_total)} bold                  icon={<IndianRupee />}    color="#dc2626" />
        </div>

        {/* Data Table */}
        <div className="bg-white border rounded-2xl overflow-hidden shadow-sm">
          <div className="px-5 py-3 border-b bg-zinc-50/60 flex items-center gap-3">
            <Calendar className="h-4 w-4 text-zinc-500" />
            <div className="flex-1">
              <div className="text-sm font-bold text-zinc-900">
                {DATE_FILTERS.find(f => f.key === filter)?.label || 'Selected period'}
              </div>
              {data?.from && data?.to && (
                <div className="text-[11px] text-zinc-500">
                  {formatDateIST(data.from)} — {formatDateIST(data.to)}
                </div>
              )}
            </div>
            <span className="text-xs text-zinc-500">{rows.length} visit{rows.length === 1 ? '' : 's'}</span>
          </div>
          {loading ? (
            <div className="py-12 text-center text-zinc-400">
              <Loader2 className="h-6 w-6 mx-auto animate-spin" />
              <p className="text-sm mt-2">Loading report…</p>
            </div>
          ) : rows.length === 0 ? (
            <div className="py-16 text-center text-zinc-400">
              <ClipboardList className="h-8 w-8 mx-auto opacity-40" />
              <p className="text-sm mt-2">No visits found for the selected period.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm" data-testid="report-table">
                <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-bold">Date/Time</th>
                    <th className="text-left px-4 py-2.5 font-bold">MRN</th>
                    <th className="text-left px-4 py-2.5 font-bold">Patient</th>
                    <th className="text-left px-4 py-2.5 font-bold">Doctor</th>
                    <th className="text-left px-4 py-2.5 font-bold">Visit</th>
                    <th className="text-left px-4 py-2.5 font-bold">Status</th>
                    <th className="text-right px-4 py-2.5 font-bold">Consult ₹</th>
                    <th className="text-right px-4 py-2.5 font-bold">Pharmacy ₹</th>
                    <th className="text-right px-4 py-2.5 font-bold">GST ₹</th>
                    <th className="text-right px-4 py-2.5 font-bold">Total ₹</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {rows.map((r, i) => (
                    <tr key={r.queue_entry_id} className="hover:bg-blue-50/30 transition" data-testid={`report-row-${i}`}>
                      <td className="px-4 py-2.5 text-[12px]">
                        <div className="font-semibold text-zinc-900">{r.date ? formatDateIST(r.date) : '—'}</div>
                        <div className="text-[10px] text-zinc-500">{r.date ? new Date(r.date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }) : ''}</div>
                      </td>
                      <td className="px-4 py-2.5 font-mono text-[11px] text-zinc-700">{r.mrn}</td>
                      <td className="px-4 py-2.5">
                        <a href={`/patients/${r.patient_id}`}
                           className="text-zinc-900 font-semibold hover:text-blue-700 hover:underline"
                           data-testid={`report-row-patient-${i}`}>
                          {r.patient_name}
                        </a>
                        <div className="text-[10px] text-zinc-500">
                          {r.patient_age ? `${r.patient_age}y` : ''} {r.patient_gender} {r.patient_phone ? `· ${r.patient_phone}` : ''}
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="text-[12px] font-semibold text-zinc-900">{r.doctor_name}</div>
                        {r.session_name && <div className="text-[10px] text-zinc-500">{r.session_name}</div>}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded">
                          {r.visit_type || 'OP'}
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`text-[10px] font-semibold capitalize border px-2 py-0.5 rounded-full ${STATUS_COLOR[r.status] || 'bg-zinc-100 text-zinc-700 border-zinc-200'}`}>
                          {(r.status || '').replace(/_/g, ' ') || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right text-[12px] font-semibold">{r.consultation_fee ? fmt(r.consultation_fee) : '—'}</td>
                      <td className="px-4 py-2.5 text-right text-[12px] font-semibold">{r.pharmacy_total ? fmt(r.pharmacy_total) : '—'}</td>
                      <td className="px-4 py-2.5 text-right text-[12px] text-zinc-500">{r.pharmacy_gst ? fmt(r.pharmacy_gst) : '—'}</td>
                      <td className="px-4 py-2.5 text-right text-[13px] font-extrabold text-zinc-900">{fmt(r.grand_total)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-zinc-50">
                  <tr className="text-[11px] font-bold text-zinc-800">
                    <td colSpan={6} className="px-4 py-2.5 text-right uppercase tracking-wider text-zinc-500">Totals</td>
                    <td className="px-4 py-2.5 text-right">{fmt(totals.consultation_total)}</td>
                    <td className="px-4 py-2.5 text-right">{fmt(totals.pharmacy_total)}</td>
                    <td className="px-4 py-2.5 text-right">{fmt(totals.pharmacy_gst)}</td>
                    <td className="px-4 py-2.5 text-right text-zinc-900 text-[13px]">{fmt(totals.grand_total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* By-doctor breakdown */}
        {totals.by_doctor && Object.keys(totals.by_doctor).length > 0 && (
          <div className="bg-white border rounded-2xl p-5 shadow-sm">
            <h3 className="text-sm font-bold text-zinc-900 mb-3" style={{ fontFamily: 'Manrope' }}>Visits by doctor</h3>
            <div className="space-y-1.5">
              {Object.entries(totals.by_doctor).sort((a, b) => b[1] - a[1]).map(([name, n]) => (
                <div key={name} className="flex items-center gap-3">
                  <div className="text-sm text-zinc-800 min-w-[140px] font-semibold">{name}</div>
                  <div className="flex-1 h-2 bg-zinc-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${(n / (totals.visits || 1)) * 100}%`, background: brand }} />
                  </div>
                  <div className="text-xs text-zinc-500 min-w-[80px] text-right">{n} visit{n === 1 ? '' : 's'}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </ReceptionLayout>
  );
}

function Kpi({ label, value, sub, icon, color, bold }) {
  return (
    <div className="bg-white border rounded-xl p-3 shadow-sm relative overflow-hidden">
      <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: color }} />
      <div className="ml-1.5 flex items-center gap-3">
        <div className="h-9 w-9 rounded-lg flex items-center justify-center text-white" style={{ background: color }}>
          {icon}
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">{label}</div>
          <div className={`text-base ${bold ? 'font-extrabold' : 'font-bold'} text-zinc-900 leading-none mt-0.5 truncate`} style={{ fontFamily: 'Manrope' }}>{value}</div>
          {sub && <div className="text-[10px] text-zinc-400 mt-1 truncate">{sub}</div>}
        </div>
      </div>
    </div>
  );
}
